import React from "react";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  Avatar,
  Box,
  Divider,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PaymentIcon from "@mui/icons-material/Payment";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate } from "react-router-dom";

import { CookieKey, deleteCookie } from "../../../../../hooks/useCookie";
import { accountService } from "./services";
import { RoleName } from "../../../enums/RoleName";
import { BRAND_COLORS } from "../../../../../constants";

interface Props {
  userData?: {
    first_name: string;
    last_name: string;
  };
  roles?: string[];
  planName?: string | null;
}

export default function AccountMenu({ userData, roles, planName }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const canAccessAccountAndBilling =
    roles?.some((r) =>
      [RoleName.ADMIN, RoleName.EDITOR].includes(r as RoleName),
    ) ?? false;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    deleteCookie(CookieKey.JWT_TOKEN);
    deleteCookie(CookieKey.REFRESH_TOKEN);
    window.location.href = "/login";
  };

  const handleBilling = async () => {
    if (!canAccessAccountAndBilling) return;
    const billingData = await accountService.fetchBilling();
    window.location.replace(billingData.url);
  };

  const handleAccount = () => {
    if (!canAccessAccountAndBilling) return;
    handleClose();
    navigate("/account");
  };

  const getInitials = () => {
    const first = userData?.first_name?.[0]?.toUpperCase() || "";
    const last = userData?.last_name?.[0]?.toUpperCase() || "";
    if (first || last) return `${first}${last}`;
    return "U";
  };

  const getFullName = () => {
    if (userData?.first_name && userData?.last_name) {
      return `${userData.first_name} ${userData.last_name}`;
    }
    if (userData?.first_name) return userData.first_name;
    return "User";
  };

  return (
    <Box>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          p: "4px",
          borderRadius: "10px",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: "13px",
              fontWeight: 700,
              backgroundColor: BRAND_COLORS.primary,
              color: "#ffffff",
              borderRadius: "10px",
            }}
          >
            {getInitials()}
          </Avatar>
          <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left" }}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: theme.palette.text.primary,
                lineHeight: 1.3,
                whiteSpace: "nowrap",
              }}
            >
              {getFullName()}
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 400,
                color: theme.palette.text.secondary,
                lineHeight: 1.3,
              }}
            >
              {planName ? `${planName} Plan` : "Free Plan"}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 15,
              color: theme.palette.text.secondary,
              display: { xs: "none", sm: "block" },
              ml: -0.25,
            }}
          />
        </Box>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "account-button",
          disablePadding: true,
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            sx: {
              width: 240,
              mt: 0.5,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        {/* User info header */}

        <Box sx={{ py: 0.5 }}>
          <MenuItem
            onClick={handleAccount}
            disabled={!canAccessAccountAndBilling}
            sx={{
              px: 2,
              py: 1.25,
              fontSize: "13px",
              color: theme.palette.text.primary,
              gap: 1.5,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <ManageAccountsIcon
              sx={{ fontSize: 18, color: theme.palette.text.secondary }}
            />
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              Account Settings
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={handleBilling}
            disabled={!canAccessAccountAndBilling}
            sx={{
              px: 2,
              py: 1.25,
              fontSize: "13px",
              color: theme.palette.text.primary,
              gap: 1.5,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <PaymentIcon
              sx={{ fontSize: 18, color: theme.palette.text.secondary }}
            />
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              Billing
            </Typography>
          </MenuItem>
        </Box>

        <Divider sx={{ my: 0 }} />

        <Box sx={{ py: 0.5 }}>
          <MenuItem
            onClick={handleLogout}
            sx={{
              px: 2,
              py: 1.25,
              gap: 1.5,
              color: BRAND_COLORS.error,
              "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.06)" },
            }}
          >
            <LogoutIcon sx={{ fontSize: 18, color: BRAND_COLORS.error }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: BRAND_COLORS.error,
              }}
            >
              Sign out
            </Typography>
          </MenuItem>
        </Box>
      </Menu>
    </Box>
  );
}
